import axios from "axios";
import Qs from "qs";
import { apiLsClientVariable, apiLsVariable } from "./utils";
import store from "../store";
import localStorageService from "./localStorage";
import router from "../router";
import { Modal } from "ant-design-vue";
import { FpjsClient } from "@fingerprintjs/fingerprintjs-pro-spa";

const error429 = "Demasiados intentos. Por favor, espere un momento.";

// Instancia única y global del cliente FingerprintJS
let fpClientInstance = null;
let fpClientInitPromise = null;

const initFingerprintClient = async () => {
  if (fpClientInitPromise) {
    return fpClientInitPromise;
  }

  if (!fpClientInitPromise && process.env.VUE_APP_FP_PUBLIC_KEY) {
    fpClientInitPromise = (async () => {
      try {
        fpClientInstance = new FpjsClient({
          loadOptions: {
            apiKey: process.env.VUE_APP_FP_PUBLIC_KEY,
            region: "us",
          },
          cacheLocation: "localstorage",
          cacheTimeInSeconds: 5 * 60 * 60, // 5 horas
        });
        // Inicializar el cliente
        await fpClientInstance.init();
        return fpClientInstance;
      } catch (error) {
        // Silently catch adblocker issues so it doesn't pollute the console/sentry
        fpClientInstance = null;
        return null;
      }
    })();
  }

  return fpClientInitPromise;
};

const getFingerprintClient = async () => {
  if (!fpClientInstance) {
    await initFingerprintClient();
  }
  return fpClientInstance;
};

axios.interceptors.request.use(
  async (config) => {
    // Verificamos si la petición declaró explícitamente requerir fingerprint (Axios a veces normaliza a minúsculas)
    const reqFp =
      config.headers["X-Require-Fingerprint"] ||
      config.headers["x-require-fingerprint"];

    if (reqFp) {
      // Borramos el header para que no se envíe al backend
      if (config.headers["X-Require-Fingerprint"])
        delete config.headers["X-Require-Fingerprint"];
      if (config.headers["x-require-fingerprint"])
        delete config.headers["x-require-fingerprint"];

      try {
        // Obtener cliente FingerprintJS e identificarse
        const fpClient = await getFingerprintClient();
        if (fpClient) {
          // Obtenemos el usuario logueado para enviarlo como identificador vinculado
          const user = store.getters["clientAuth/getLoggedUser"];
          const email = user?.email || null;

          const visitorData = await fpClient.getVisitorData(
            email ? { linkedId: email } : {}
          );

          if (visitorData) {
            config.headers["X-Fingerprint-Visitor-ID"] = visitorData.visitorId;
            config.headers["X-Fingerprint-Request-ID"] = visitorData.requestId;
          }
        }
      } catch (error) {
        // Silenciamos el error para que la petición original siga su curso si falla FP
        console.warn("Fingerprint skip:", error.message);
      }
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

const showError429 = () => {
  Modal.error({
    class: "error-429",
    title: "Error",
    content: error429,
    okText: "Aceptar",
    onOk: () => {
      // Optionally, you can add any additional logic here
    },
  });
};

export const myToken = () => {
  let session =
    localStorageService.get(
      window.location.pathname?.includes("admin")
        ? apiLsVariable
        : apiLsClientVariable
    ) ?? null;
  if (session) {
    let { token = null } = JSON.parse(window.atob(session));
    return token;
  }
  return null;
};

export const restClient = (
  route,
  method,
  data = null,
  params = {},
  extraHeaders = {}
) => {
  const token = extraHeaders?.token || myToken();
  if (!token)
    Promise.reject({ has_error: true, code: 401, message: "Accesso Denegado" });
  return axios({
    url: `${route}`,
    method: method,
    data: data,
    headers: {
      Authorization: `Bearer ${token}`,
      ...extraHeaders,
    },
    json: true,
    params: params,
    paramsSerializer: function (params) {
      return Qs.stringify(params, { arrayFormat: "brackets" });
    },
  })
    .then((response) => {
      return Promise.resolve(response.data);
    })
    .catch(async (error) => {
      if (error.response) {
        if ([401].includes(error.response.status)) {
          const isAdminPage = window.location.pathname?.includes("admin");
          if (
            token &&
            !route?.includes("logout") &&
            !window.location.pathname?.includes("login")
          ) {
            await store.dispatch(
              `${isAdminPage ? "admin" : "client"}Auth/handleLogout`
            );
          }
          if (
            !["/login", "/admin", "/register"].includes(
              window.location.pathname
            )
          )
            router.push({ name: isAdminPage ? "admin" : "login" });
        }
        if (error.response?.status === 429) {
          showError429();
        }
        return Promise.reject(error);
      } else if (error.request) {
        return Promise.reject(getUnknownError());
      } else {
        return Promise.reject(getUnknownError());
      }
    });
};

export const downloadFile = (route, fileName) => {
  const token = myToken();
  // if(!token) throw {has_error: true, code: 401, message: 'Accesso Denegado'};
  return axios({
    url: `${route}`,
    method: "GET",
    responseType: "blob", // important,
    headers: {
      Authorization: `Bearer ${token}`,
      "Cache-Control": "no-cache",
    },
  })
    .then((response) => {
      const url = window.URL.createObjectURL(
        new Blob([response.data], { type: "application/octet-stream" })
      );
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${fileName}`); //or any other extension
      link.setAttribute("target", "_blank"); //or any other extension
      document.body.appendChild(link);
      link.click();
      window.URL.revokeObjectURL(url);
    })
    .catch((error) => {
      return Promise.reject(error);
    });
};

export const previewFile = (route) => {
  const token = myToken();
  if (!token) throw { has_error: true, code: 401, message: "Accesso Denegado" };
  return axios({
    url: `${route}`,
    method: "GET",
    responseType: "blob", // important,
    headers: {
      Authorization: `Bearer ${token}`,
      "Cache-Control": "no-cache",
    },
  })
    .then((response) => {
      const url = window.URL.createObjectURL(
        new Blob([response.data], { type: "application/pdf" })
      );
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("target", "_blank"); //or any other extension
      document.body.appendChild(link);
      link.click();
    })
    .catch((error) => {
      return Promise.reject(error);
    });
};

export const restClientWithOutAuthentication = (
  route,
  method,
  data = null,
  params = {},
  extraHeaders = {}
) => {
  return axios({
    url: `${route}`,
    method: method,
    headers: {
      ...extraHeaders,
    },
    data: data,
    json: true,
    params: params,
    paramsSerializer: function (params) {
      return Qs.stringify(params, { arrayFormat: "brackets" });
    },
  })
    .then((response) => {
      return Promise.resolve(response.data);
    })
    .catch((error) => {
      if (error.response) {
        if (error.response?.status === 429) {
          showError429();
        }
        return Promise.reject(error);
      } else if (error.request) {
        return Promise.reject(getUnknownError());
      } else {
        return Promise.reject(getUnknownError());
      }
    });
};

export const restClientAll = (
  route,
  method,
  data = null,
  params = {},
  extraHeaders = {}
) => {
  const token = myToken();
  const headers = token ? { Authorization: `Bearer ${token}` } : {};
  return axios({
    url: `${route}`,
    method: method,
    headers: {
      ...headers,
      ...extraHeaders,
    },
    data: data,
    json: true,
    params: params,
    paramsSerializer: function (params) {
      return Qs.stringify(params, { arrayFormat: "brackets" });
    },
  })
    .then((response) => {
      return Promise.resolve(response.data);
    })
    .catch(async (error) => {
      if (error.response) {
        if ([401].includes(error.response.status)) {
          const isAdminPage = window.location.pathname?.includes("admin");
          if (
            token &&
            !route?.includes("logout") &&
            !window.location.pathname?.includes("login")
          ) {
            await store.dispatch(
              `${isAdminPage ? "admin" : "client"}Auth/handleLogout`
            );
          }
          if (
            !["/login", "/admin", "/register"].includes(
              window.location.pathname
            )
          )
            router.push({ name: isAdminPage ? "admin" : "login" });
        }

        if (error.response.status === 429) {
          showError429();
        }
        return Promise.reject(error);
      } else if (error.request) {
        return Promise.reject(getUnknownError());
      } else {
        return Promise.reject(getUnknownError());
      }
    });
};

/* Unknow error */
const getUnknownError = () => ({
  has_error: true,
  additional_errors: null,
  data: null,
  code: 500,
  message:
    "El servidor no puede procesar la solicitud por un motivo desconocido.",
  redirect: null,
});

export { initFingerprintClient };
